// Small subcomponents for `AutoTuneTaskNode`. Extracted so the main
// node component stays under the file-length lint budget; nothing here
// is reused outside the node.

import { Spinner, Stack, Typography } from "@altimateai/lego";
import { cn } from "@/lib/utils";
import type { ClusterSpec } from "./AutoTuneJobDag.types";

const formatCostPerHr = (cost: number) => `$${cost.toFixed(2)}/hr`;

const formatWorkers = (spec: ClusterSpec): string => {
  if (spec.workers) return spec.workers;
  if (spec.min === undefined || spec.min === null) return "—";
  if (spec.max === undefined || spec.max === null || spec.max === spec.min) {
    return `${spec.min} workers`;
  }
  return `${spec.min}–${spec.max} workers`;
};

interface ClusterPanelProps {
  label: string;
  spec: ClusterSpec;
  emphasis?: boolean;
  /** When true, render Worker + Driver as two labelled rows even if
   *  the values are identical. Used on Recommended / Apply-pending /
   *  Applied cards so the user sees both recs explicitly. Defaults to
   *  false (collapse-when-equal) for compact static views. */
  alwaysSplit?: boolean;
}

// Current / Proposed cluster spec block on each task card. Renders two
// labelled rows (Worker / Driver) when the worker and driver instance
// types differ — the post-#5050 split — or when ``alwaysSplit`` is set
// so cards in actionable states surface both recs explicitly.
export const ClusterPanel = ({ label, spec, emphasis, alwaysSplit }: ClusterPanelProps) => {
  const worker = spec.workerInstance ?? spec.instance;
  const driver = spec.driverInstance ?? worker;
  const showSplit = Boolean(worker) && Boolean(driver) && (alwaysSplit || worker !== driver);
  const instanceColor = emphasis ? "al-text-action-green" : "al-text-dark";

  return (
    <Stack
      direction="column"
      gap="xs"
      className={cn(
        // Equal-width Current/Proposed columns. The card itself is sized
        // (see ``computeNodeWidth``) to fit the longest instance name on
        // one line, so each column has room for its full name.
        "al-flex-1 al-rounded-md al-border al-px-3 al-py-2",
        emphasis
          ? "al-bg-bg-green al-border-action-green/30"
          : "al-bg-bg-gray al-border-stroke-light"
      )}
    >
      <Typography size="xs" className="al-text-muted-foreground al-uppercase">
        {label}
      </Typography>
      {showSplit ? (
        <Stack direction="column" gap="xxs">
          <Stack direction="row" gap="xs" align="baseline">
            <Typography size="xs" className="al-text-muted-foreground al-w-12 al-shrink-0">
              Worker
            </Typography>
            <Typography
              size="sm"
              weight="medium"
              className={cn(instanceColor, "al-whitespace-nowrap")}
            >
              {worker}
            </Typography>
          </Stack>
          <Stack direction="row" gap="xs" align="baseline">
            <Typography size="xs" className="al-text-muted-foreground al-w-12 al-shrink-0">
              Driver
            </Typography>
            <Typography
              size="sm"
              weight="medium"
              className={cn(instanceColor, "al-whitespace-nowrap")}
            >
              {driver}
            </Typography>
          </Stack>
        </Stack>
      ) : (
        <Typography size="sm" weight="medium" className={cn(instanceColor, "al-whitespace-nowrap")}>
          {spec.instance}
        </Typography>
      )}
      <Stack direction="row" gap="xs" align="center">
        {emphasis && spec.highlightMinWorkers && typeof spec.min === "number" && !spec.workers ? (
          // Positive Worker Sizing recommendation — surface the proposed
          // minimum-worker count in the recommendation accent (green),
          // matching the emphasised instance name above. The max + unit
          // stay muted so only the changed value reads as actionable.
          <Typography size="xs" className="al-text-muted-foreground">
            <span className="al-text-action-green al-font-medium">{spec.min}</span>
            {typeof spec.max === "number" && spec.max !== spec.min
              ? `–${spec.max} workers`
              : " workers"}
          </Typography>
        ) : (
          <Typography size="xs" className="al-text-muted-foreground">
            {formatWorkers(spec)}
          </Typography>
        )}
        {/* Show the cost row only when we have a real priced source.
            ``buildNoRecNode`` sets ``costPerHr: null`` for tasks with
            no recommendation; rendering "$0.00/hr" would read as a
            real value. ``null`` is the sentinel; ``> 0`` also guards
            against a degenerate rec with cost 0. */}
        {spec.costPerHr != null && spec.costPerHr > 0 ? (
          <>
            <Typography size="xs" className="al-text-muted-foreground">
              ·
            </Typography>
            <Typography size="xs" className="al-text-muted-foreground">
              {formatCostPerHr(spec.costPerHr)}
            </Typography>
          </>
        ) : null}
      </Stack>
      {/* Auto-termination idle window. Only the all-purpose-cluster DAG
          supplies ``autoTerminationMinutes`` — job-task cards leave it
          undefined, so this row never renders for them. The delta reads
          across the Current → Proposed arrow (e.g. 180 min → 45 min). */}
      {spec.autoTerminationMinutes != null ? (
        <Stack direction="row" gap="xs" align="center">
          <Typography size="xs" className="al-text-muted-foreground">
            Auto-terminate
          </Typography>
          <Typography
            size="xs"
            weight="medium"
            className={emphasis ? "al-text-action-green" : "al-text-dark"}
          >
            {spec.autoTerminationMinutes} min
          </Typography>
        </Stack>
      ) : null}
    </Stack>
  );
};

interface SavingIndicatorProps {
  /** Identifier used to scope the test id and aria label. */
  taskId: string;
}

// Spinner + "Saving…" label rendered next to the per-task switch while
// a toggle / cascade is in flight. Brand color so it reads as an
// activity signal rather than disabled muted state.
// eslint-disable-next-line react/no-multi-comp
export const TaskSavingIndicator = ({ taskId }: SavingIndicatorProps) => (
  <Stack
    direction="row"
    align="center"
    gap="xs"
    className="al-text-action-brand"
    role="status"
    aria-live="polite"
    aria-label={`Saving Auto Tune for ${taskId}`}
  >
    <Spinner size="sm" aria-hidden />
    <Typography size="xs" weight="medium">
      Saving…
    </Typography>
  </Stack>
);
